import { useState, useEffect } from 'react'
import './DurationInput.css'

interface DurationInputProps {
  value: string
  onChange: (value: string) => void
  min: string | null
  max: string | null
  disabled?: boolean
  className?: string
}

export default function DurationInput({ value, onChange, min, max, disabled, className }: DurationInputProps) {
  const parseValue = (val: string) => {
    const parts = val?.split(':') || [];
    return {
      h: parts[0] || '00',
      m: parts[1] || '00',
      s: parts[2] || '00',
    };
  };

  const initial = parseValue(value);
  const [hours, setHours] = useState(initial.h);
  const [minutes, setMinutes] = useState(initial.m);
  const [seconds, setSeconds] = useState(initial.s);

  const toSeconds = (val: string) => {
    const [h, m, s] = val.split(':').map(Number);
    return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
  };

  const fromSeconds = (totalSecs: number) => {
    const h = Math.floor(totalSecs / 3600);
    const m = Math.floor((totalSecs % 3600) / 60);
    const s = totalSecs % 60;
    return {
      h: String(h).padStart(2, '0'),
      m: String(m).padStart(2, '0'),
      s: String(s).padStart(2, '0')
    };
  };

  useEffect(() => {
    if (value) {
      const { h, m, s } = parseValue(value);
      if (parseInt(h, 10) !== parseInt(hours, 10)) setHours(h);
      if (parseInt(m, 10) !== parseInt(minutes, 10)) setMinutes(m);
      if (parseInt(s, 10) !== parseInt(seconds, 10)) setSeconds(s);
    }
  }, [value]);

  const handleUpdate = (newH: string, newM: string, newS: string, forceClamp = false) => {
    const combinedStr = `${newH.padStart(2, '0')}:${newM.padStart(2, '0')}:${newS.padStart(2, '0')}`;
    let totalSecs = toSeconds(combinedStr);

    const minSecs = min ? toSeconds(min) : 0;
    const maxSecs = max ? toSeconds(max) : 99 * 3600 + 59 * 60 + 59;

    if (totalSecs < minSecs) totalSecs = minSecs;
    if (totalSecs > maxSecs) totalSecs = maxSecs;

    const final = fromSeconds(totalSecs);

    if (forceClamp) {
      setHours(final.h);
      setMinutes(final.m);
      setSeconds(final.s);
    } else {
      setHours(newH);
      setMinutes(newM);
      setSeconds(newS);
    }

    const finalString = `${final.h}:${final.m}:${final.s}`;
    if (finalString !== value) {
      onChange(finalString);
    }
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'h' | 'm' | 's', limit: number) => {
    let val = e.target.value.replace(/\D/g, '');
    if (val.length > 2) val = val.slice(-2);
    if (parseInt(val || '0', 10) > limit) val = String(limit);

    if (type === 'h') handleUpdate(val, minutes, seconds);
    if (type === 'm') handleUpdate(hours, val, seconds);
    if (type === 's') handleUpdate(hours, minutes, val);
  };

  return (
    <div className={`duration-input ${className || ''}`}>
      <div className="duration-field">
        <input
          type="text"
          value={hours}
          onChange={(e) => onInputChange(e, 'h', 99)}
          onBlur={() => handleUpdate(hours.padStart(2, '0'), minutes, seconds, true)}
          disabled={disabled}
          className="duration-segment"
        />
        <label>Hours</label>
      </div>
      <span>:</span>
      <div className="duration-field">
        <input
          type="text"
          value={minutes}
          onChange={(e) => onInputChange(e, 'm', 59)}
          onBlur={() => handleUpdate(hours, minutes.padStart(2, '0'), seconds, true)}
          disabled={disabled}
          className="duration-segment"
        />
        <label>Minutes</label>
      </div>
      <span>:</span>
      <div className="duration-field">
        <input
          type="text"
          value={seconds}
          onChange={(e) => onInputChange(e, 's', 59)}
          onBlur={() => handleUpdate(hours, minutes, seconds.padStart(2, '0'), true)}
          disabled={disabled}
          className="duration-segment"
        />
        <label>Seconds</label>
      </div>
    </div>
  );
}
